import { tacticKey } from '@bloo-team/api-client'
import type { CmCategory, PostureTopEntry } from '@bloo-team/api-client'
import type { Signal } from '../lib/signal-model'

/**
 * Pure derivations behind Vantage's home widgets — every number the widgets
 * draw comes from an existing read (docs/home.md §Data joins); nothing here
 * invents a value the wire did not carry.
 */

/** The home window: widgets read the last 24h (the signals `1d` duration). */
export const HOME_WINDOW = '1d' as const

const HOUR = 3_600_000

/** Open cases = every status category except closed (same rule as the overview). */
export function openCaseCount(counts: Record<CmCategory, number> | undefined): number | null {
  if (!counts) return null
  return counts.new + counts.in_progress + counts.on_hold
}

/** A case as the activity feed reads it (V2 list rows). */
export interface FeedCase {
  id: string
  title: string
  severity: string
  createdAt: number | null
}

export interface DeltaView {
  label: string
  dir: 'up' | 'down' | 'flat'
}

/** `percent_diff` → the `▲12%` / `▼4%` / `– flat` chip; null when the server omitted it. */
export function deltaView(pct: number | null | undefined): DeltaView | null {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null
  const r = Math.round(pct)
  if (r === 0) return { label: '– flat', dir: 'flat' }
  return r > 0 ? { label: `▲${r}%`, dir: 'up' } : { label: `▼${Math.abs(r)}%`, dir: 'down' }
}

/** Compact age: `12s`, `4m`, `3h`, `2d`. */
export function ageLabel(at: number, now: number): string {
  const s = Math.max(0, Math.round((now - at) / 1000))
  if (s < 60) return `${s}s`
  if (s < HOUR / 1000) return `${Math.round(s / 60)}m`
  if (s < 86_400) return `${Math.round(s / 3600)}h`
  return `${Math.round(s / 86_400)}d`
}

export type FeedSeverity = 'critical' | 'high' | 'medium' | 'low'

export interface FeedRow {
  key: string
  at: number
  kind: 'Signal' | 'Case'
  text: string
  severity: FeedSeverity | undefined
}

const FEED_SEVERITIES: ReadonlySet<string> = new Set(['critical', 'high', 'medium', 'low'])

function feedSeverity(s: string): FeedSeverity | undefined {
  const k = s.trim().toLowerCase()
  return FEED_SEVERITIES.has(k) ? (k as FeedSeverity) : undefined
}

function primaryEntity(s: Signal): string | null {
  const e = s.entities.find((x) => x.role === 'Target') ?? s.entities[0]
  return e ? e.value : null
}

/**
 * The activity feed: signals and newly opened cases in the window, newest
 * first. There is no global event stream — case assignment / status moves
 * live per case in `cases/history` — so the feed says only what the two
 * list reads carry. Cases with no parseable creation time are left out
 * rather than pinned to "now".
 */
export function feedRows(signals: Signal[], cases: FeedCase[], now: number, limit = 12, windowMs = 24 * HOUR): FeedRow[] {
  const since = now - windowMs
  const rows: FeedRow[] = []
  for (const s of signals) {
    if (s.at < since) continue
    const entity = primaryEntity(s)
    rows.push({
      key: `s:${s.uuid}`,
      at: s.at,
      kind: 'Signal',
      text: entity ? `${s.detectionName} · ${entity}` : s.detectionName,
      severity: feedSeverity(s.severity),
    })
  }
  for (const c of cases) {
    if (c.createdAt === null || c.createdAt < since) continue
    rows.push({ key: `c:${c.id}`, at: c.createdAt, kind: 'Case', text: `${c.title} opened`, severity: feedSeverity(c.severity === 'informational' ? 'info' : c.severity) })
  }
  return rows.sort((a, b) => b.at - a.at || a.key.localeCompare(b.key)).slice(0, limit)
}

export interface TacticCell {
  name: string
  count: number
  /** 0..100 intensity against the busiest tactic. */
  v: number
}

/**
 * Tactic activity strip: the ATT&CK tactic skeleton (`attack/matrix`, a
 * light GET) joined to the window's per-tactic signal counts
 * (`cases/stats` top tactics). Joined on `tacticKey` because the two reads
 * spell tactics differently ("Defence Evasion"). `Unclassified` is dropped
 * from the skeleton — it is a bucket, not a tactic. `top_tactics` is capped
 * at ten server-side, so a tactic outside the top ten reads as 0.
 */
export function tacticActivity(tactics: string[], top: PostureTopEntry[]): TacticCell[] {
  const counts = new Map<string, number>()
  for (const t of top) counts.set(tacticKey(t.name), (counts.get(tacticKey(t.name)) ?? 0) + t.count)
  const cells = tactics
    .filter((t) => tacticKey(t) !== 'unclassified')
    .map((name) => ({ name, count: counts.get(tacticKey(name)) ?? 0 }))
  const max = Math.max(0, ...cells.map((c) => c.count))
  return cells.map((c) => ({ ...c, v: max ? Math.round((c.count / max) * 100) : 0 }))
}
